import { useEffect, useRef } from 'react';

/**
 * Hook to proactively monitor and refresh Cognito tokens before expiration.
 *
 * Cognito ID tokens expire after 1 hour. This hook:
 * 1. Monitors the token expiration time
 * 2. Proactively refreshes the token 5 minutes before expiry
 * 3. Uses the refresh token to get new access/id tokens
 *
 * This prevents users from being logged out due to idle sessions.
 *
 * @param {Object} userStore - MobX UserStore instance
 */
export const useTokenRefresh = (userStore) => {
  const refreshTimerRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Only set up monitoring if user is authenticated
    if (!userStore.isAuthenticated || !userStore.expiredAt) {
      return;
    }

    const scheduleTokenRefresh = () => {
      // Clear any existing timers
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const expiresAt = userStore.expiredAt * 1000; // Convert to milliseconds
      if (!expiresAt || isNaN(expiresAt)) {
        console.warn('[TokenRefresh] expiredAt is invalid, skipping refresh schedule');
        return;
      }
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Refresh 5 minutes (300000ms) before expiration
      const REFRESH_BUFFER_MS = 5 * 60 * 1000;
      const timeUntilRefresh = timeUntilExpiry - REFRESH_BUFFER_MS;

      console.debug('[TokenRefresh] Token expires at', new Date(expiresAt).toISOString(),
                    `(in ${Math.round(timeUntilExpiry / 1000)}s), will refresh in ${Math.round(timeUntilRefresh / 1000)}s`);

      // If token is already expired or about to expire, refresh immediately
      if (timeUntilRefresh <= 0) {
        console.debug('[TokenRefresh] Token expired or about to expire, refreshing immediately');
        userStore.restoreSession(true, true)
          .then((session) => {
            if (session) {
              console.debug('[TokenRefresh] Token refreshed successfully');
              scheduleTokenRefresh();
            }
          })
          .catch(error => {
            console.error('[TokenRefresh] Failed to refresh token:', error);
            // Don't schedule another refresh if refresh failed
            // User will be logged out naturally on next API call
          });
      } else {
        // Schedule refresh before token expires
        refreshTimerRef.current = setTimeout(() => {
          console.debug('[TokenRefresh] Proactively refreshing token before expiration');
          userStore.restoreSession(true, true)
            .then((session) => {
              if (session) {
                console.debug('[TokenRefresh] Token refreshed successfully');
                scheduleTokenRefresh();
              }
            })
            .catch(error => {
              console.error('[TokenRefresh] Failed to refresh token:', error);
            });
        }, timeUntilRefresh);
      }
    };

    // Initial schedule
    scheduleTokenRefresh();

    // Also set up a periodic check every 5 minutes to handle edge cases
    // (e.g., token was refreshed by a request, but we didn't notice)
    checkIntervalRef.current = setInterval(() => {
      if (userStore.isAuthenticated && userStore.expiredAt) {
        const expiresAt = userStore.expiredAt * 1000;
        if (!expiresAt || isNaN(expiresAt)) return;
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // If we're within 5 minutes of expiry and no timer is set, schedule a refresh
        if (timeUntilExpiry < 5 * 60 * 1000 && !refreshTimerRef.current) {
          console.debug('[TokenRefresh] Periodic check detected near-expiry token without scheduled refresh, scheduling now');
          scheduleTokenRefresh();
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    // Cleanup on unmount or when auth state changes
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [userStore, userStore.isAuthenticated, userStore.expiredAt]);

  return null;
};